import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React from "react";
import Map, { Layer, NavigationControl, Source } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../../config";
import Helper from "../../../Helper";
import { MapLayer } from "../../../models/MapLayer";
import { MapObject } from "../../../models/MapObject";

// Where a PDF's map looks. The zoom is for a map the width of the capture (see PdfMapCapture); a map drawn narrower
// shows the same area at a lower zoom (see zoomForWidth).
export interface PdfMapCamera {
    center: [number, number];
    zoom: number;
    bearing: number;
}

export const PDF_MAP_MAX_ZOOM = 17;

// The zoom that shows the same area as `zoom` does at `fromWidth`, on a map `toWidth` wide.
export const zoomForWidth = (zoom: number, fromWidth: number, toWidth: number) => zoom + Math.log2(toWidth / fromWidth);

export const pdfMapHasGeotiff = (project: any) => project?.geotiff?.status === "complete";

// An image pinned to a spot on the map, e.g. the GC's logo on the jobsite.
export interface PdfMapLogo {
    // [longitude, latitude]
    coordinates: [number, number];
    // A data URL, so the map can draw it without a cross-origin fetch.
    image: string;
}

export const pdfMapBounds = (mapObjects: MapObject[], logo?: PdfMapLogo | null) => {
    let bounds = new mapboxgl.LngLatBounds();
    mapObjects.forEach((object) => {
        if (object.coordinates) bounds = Helper.addToBounds(object, bounds);
    });
    if (logo) bounds.extend(logo.coordinates);
    return bounds;
};

const LOGO_ID = "pdfMapLogo";
// The logo's tile on the map, in CSS pixels, and the resolution it's drawn at so it stays sharp in the snapshot.
const LOGO_SIZE = 44;
const LOGO_PIXEL_RATIO = 2;

// Draws the logo contained in a white rounded tile, so it reads over any map, and adds it to the map's images. Resolves
// false if the image won't load, and the logo is left off.
const addLogoImage = (map: mapboxgl.Map, image: string) =>
    new Promise<boolean>((resolve) => {
        const element = new Image();
        element.onload = () => {
            const size = LOGO_SIZE * LOGO_PIXEL_RATIO;
            const border = 2 * LOGO_PIXEL_RATIO;
            const inset = 5 * LOGO_PIXEL_RATIO;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const context = canvas.getContext("2d");
            if (!context) return resolve(false);
            context.beginPath();
            context.roundRect(border / 2, border / 2, size - border, size - border, 8 * LOGO_PIXEL_RATIO);
            context.fillStyle = "#ffffff";
            context.fill();
            context.lineWidth = border;
            context.strokeStyle = "#d1d5db";
            context.stroke();
            const scale = Math.min((size - inset * 2) / element.width, (size - inset * 2) / element.height);
            const drawWidth = element.width * scale;
            const drawHeight = element.height * scale;
            context.drawImage(element, (size - drawWidth) / 2, (size - drawHeight) / 2, drawWidth, drawHeight);
            if (!map.hasImage(LOGO_ID)) {
                map.addImage(LOGO_ID, context.getImageData(0, 0, size, size), { pixelRatio: LOGO_PIXEL_RATIO });
            }
            resolve(true);
        };
        element.onerror = () => resolve(false);
        element.src = image;
    });

interface Props {
    mapObjects: MapObject[];
    project: any;
    mapStyle: string;
    width: number;
    height: number;
    interactive: boolean;
    // Whether the project's GeoTIFF is drawn under the items, when it has one. Defaults to true.
    showGeotiff?: boolean;
    // Drawn on top of the items.
    logo?: PdfMapLogo | null;
    // Called once the style and the item icons have loaded, before the items are drawn. `logoDrawn` is false when there's
    // no logo or its image wouldn't load, so framing can leave its spot out.
    onReady: (map: mapboxgl.Map, logoDrawn: boolean) => void;
}

const SOURCE_ID = "pdfMap";

// <Source> hands each direct child a `source` prop, which React warns about on a Fragment. The layers name their source
// themselves, so this just takes and drops it.
const ObjectLayers: React.FC<{ children: React.ReactNode; source?: string }> = ({ children }) => <>{children}</>;

// The map as a PDF prints it: the given items and logo, plus the project's GeoTIFF and enabled layers. Used off-screen for the
// snapshot (PdfMapCapture) and on-screen to frame it (PdfMapFrameEditor), so both draw the same thing.
const PdfMapView: React.FC<Props> = ({
    mapObjects,
    project,
    mapStyle,
    width,
    height,
    interactive,
    showGeotiff = true,
    logo,
    onReady,
}) => {
    const [loadedMap, setLoadedMap] = React.useState<mapboxgl.Map | null>(null);
    const [imagesReady, setImagesReady] = React.useState(false);
    const [logoReady, setLogoReady] = React.useState(false);
    const [metersPerPixel, setMetersPerPixel] = React.useState(1);

    const geoJson = React.useMemo(() => Helper.generateMapGeoJson(mapObjects), [mapObjects]);

    const layersFor = (map: mapboxgl.Map, object: MapObject) => {
        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, SOURCE_ID, map, "visible", 0.25);
            case "lineString":
                return Helper.addLine(
                    object,
                    SOURCE_ID,
                    "visible",
                    object.iconWidth
                        ? Helper.getIconRealWorldSize(object, metersPerPixel)
                        : Helper.defaultIconSizeArray(true),
                    map
                );
            case "polygon":
                return Helper.addPolygon(object, SOURCE_ID, "visible", 0.25, map);
            default:
                return null;
        }
    };

    const enabledLayers: MapLayer[] = project?.map_layers?.filter((layer: MapLayer) => layer.enabled) || [];

    return (
        <Map
            initialViewState={{
                longitude: project?.lon_override || project?.lon || -97.314835,
                latitude: project?.lat_override || project?.lat || 37.697948,
                zoom: PDF_MAP_MAX_ZOOM,
            }}
            mapStyle={mapStyle}
            mapboxAccessToken={globalAppConfig.MAP_BOX_ACCESS_TOKEN}
            preserveDrawingBuffer={true}
            interactive={interactive}
            // Turning is allowed, tilting isn't: a printed map reads best flat.
            maxPitch={0}
            pitchWithRotate={false}
            touchPitch={false}
            attributionControl={true}
            style={{ width, height }}
            // Line icons are sized in real-world metres, so they're resized as the zoom changes.
            onMoveEnd={(event) => setMetersPerPixel(Helper.getMapMaxMetersPerPixelAtZoom(event.target))}
            onLoad={(event) => {
                const map = event.target;
                setLoadedMap(map);
                let logoDrawn = false;
                Promise.all([
                    Helper.addMarkerImages(map).catch(() => undefined),
                    logo
                        ? addLogoImage(map, logo.image).then((added) => {
                              logoDrawn = added;
                              setLogoReady(added);
                          })
                        : undefined,
                ]).finally(() => {
                    setMetersPerPixel(Helper.getMapMaxMetersPerPixelAtZoom(map));
                    setImagesReady(true);
                    onReady(map, logoDrawn);
                });
            }}
        >
            {interactive && <NavigationControl showCompass showZoom visualizePitch={false} />}
            {showGeotiff && pdfMapHasGeotiff(project) && (
                <Source
                    type="raster"
                    id={`${project.geotiff.id}_pdf_geotiff`}
                    url={`mapbox://nickdreiling.${project.geotiff.geotiff_name}`}
                >
                    <Layer
                        id={`${project.geotiff.id}_pdf_geotiff`}
                        slot="middle"
                        type="raster"
                        source={`${project.geotiff.id}_pdf_geotiff`}
                    />
                </Source>
            )}
            {enabledLayers.map((layer) => (
                <Source key={layer.id} type="raster" id={`pdf_layer_${layer.id}`} tiles={[layer.url]}>
                    <Layer id={`pdf_layer_${layer.id}`} slot="middle" type="raster" source={`pdf_layer_${layer.id}`} />
                </Source>
            ))}
            {imagesReady && loadedMap && (
                <Source id={SOURCE_ID} type="geojson" data={geoJson}>
                    {mapObjects.map((object) => (
                        <ObjectLayers key={object.uuid}>{layersFor(loadedMap, object)}</ObjectLayers>
                    ))}
                </Source>
            )}
            {imagesReady && logoReady && logo && (
                <Source
                    id={LOGO_ID}
                    type="geojson"
                    data={{
                        type: "Feature",
                        geometry: { type: "Point", coordinates: logo.coordinates },
                        properties: {},
                    }}
                >
                    <Layer
                        id={LOGO_ID}
                        type="symbol"
                        source={LOGO_ID}
                        layout={{ "icon-image": LOGO_ID, "icon-allow-overlap": true, "icon-ignore-placement": true }}
                    />
                </Source>
            )}
        </Map>
    );
};

export default PdfMapView;
